import { useMemo, useState } from "react";
import { Paperclip } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

import { belegOeffnen } from "./daten";
import { cent } from "./kern/typen";
import { Auswahl, fmtCent, fmtDatum, type TabProps } from "./teile";

const SEITE = 50;

// Suche läuft nur im Browser, der Buchungstext geht nie in eine Abfrage.
export const KontoSpalte = ({ daten }: TabProps) => {
  const [kontoId, setKontoId] = useState("");
  const [suche, setSuche] = useState("");
  const [ohneBeleg, setOhneBeleg] = useState(false);
  const [anzahl, setAnzahl] = useState(SEITE);
  const belegZu = useMemo(
    () => new Map(daten.belege.map((b) => [b.buchung_id, b])),
    [daten.belege],
  );

  const liste = useMemo(() => {
    const wort = suche.trim().toLowerCase();
    return daten.buchungen
      .filter((b) => b.konto_id !== null)
      .filter((b) => !kontoId || b.konto_id === Number(kontoId))
      .filter((b) => !ohneBeleg || (!belegZu.has(b.id) && !b.beleg))
      .filter(
        (b) =>
          !wort ||
          `${b.partner} ${b.text} ${b.beleg ?? ""} ${b.betrag}`
            .toLowerCase()
            .includes(wort),
      )
      .reverse();
  }, [daten.buchungen, kontoId, suche, ohneBeleg, belegZu]);

  const summe = liste.reduce((s, b) => s + cent(b.betrag), 0);

  return (
    <section
      aria-label="Konto"
      className="space-y-3 rounded-lg border bg-card p-4"
    >
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="font-semibold">Konto</h2>
        <span className="text-lg font-semibold tabular-nums">
          {fmtCent(summe)}
        </span>
      </div>
      {daten.konten.length > 1 && (
        <Auswahl
          aria-label="Konto wählen"
          value={kontoId}
          onChange={(e) => setKontoId(e.target.value)}
        >
          <option value="">Alle Konten</option>
          {daten.konten.map((k) => (
            <option key={k.id} value={k.id}>
              {k.name}
            </option>
          ))}
        </Auswahl>
      )}
      <Input
        aria-label="Zahlungen suchen"
        placeholder="Suchen"
        value={suche}
        onChange={(e) => setSuche(e.target.value)}
      />
      <label className="flex items-center gap-2 text-sm">
        <Switch checked={ohneBeleg} onCheckedChange={setOhneBeleg} /> Nur ohne
        Beleg
      </label>

      {liste.length === 0 && (
        <p className="text-sm text-muted-foreground">Keine Zahlungen.</p>
      )}
      <ul className="max-h-[32rem] divide-y overflow-y-auto text-sm">
        {liste.slice(0, anzahl).map((b) => {
          const beleg = belegZu.get(b.id);
          return (
            <li
              key={b.id}
              className="flex items-start justify-between gap-2 py-1.5"
            >
              <div className="min-w-0">
                <div className="truncate">{b.partner || b.text}</div>
                {b.partner && b.text && (
                  <div
                    className="truncate text-xs text-muted-foreground"
                    title={b.text}
                  >
                    {b.text}
                  </div>
                )}
                <div className="text-xs text-muted-foreground">
                  {fmtDatum(b.datum)}
                  {b.kategorie && (
                    <Badge variant="secondary" className="ml-1">
                      {b.kategorie}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {beleg && (
                  <button
                    type="button"
                    title={beleg.dateiname}
                    aria-label="Beleg zur Zahlung ansehen"
                    className="cursor-pointer text-muted-foreground"
                    onClick={() => belegOeffnen(beleg.pfad)}
                  >
                    <Paperclip className="h-4 w-4" />
                  </button>
                )}
                <span
                  className={`tabular-nums ${b.betrag > 0 ? "text-green-700 dark:text-green-400" : ""}`}
                >
                  {fmtCent(cent(b.betrag))}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
      {liste.length > anzahl && (
        <Button
          variant="link"
          className="h-auto p-0"
          onClick={() => setAnzahl(anzahl + SEITE)}
        >
          Weitere zeigen ({liste.length - anzahl})
        </Button>
      )}
    </section>
  );
};
